import React, { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';
import { Users, Trash2, Mail } from 'lucide-react';
import { motion } from 'framer-motion';

const SubscribersManager = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubscribers();
  }, []);

  const loadSubscribers = async () => {
    try {
      const res = await adminApi.getSubscribers();
      setSubscribers(res.data);
    } catch (error) {
      console.error('Failed to load subscribers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Remove this subscriber?')) {
      try {
        await adminApi.deleteSubscriber(id);
        setSubscribers(subscribers.filter(s => s.id !== id));
      } catch (error) {
        alert('Failed to remove subscriber');
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyber-yellow"></div></div>;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="subscribers-manager">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Subscribers</h2>
        <span className="text-text-muted text-sm font-mono">{subscribers.length} total</span>
      </div>

      {subscribers.length === 0 ? (
        <div className="terminal-grid p-12 text-center">
          <Users size={48} className="mx-auto mb-4 text-text-muted opacity-40" />
          <p className="text-text-muted">No subscribers yet. They will appear here when users subscribe via the newsletter form.</p>
        </div>
      ) : (
        <div className="terminal-grid overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-5 py-3 text-xs text-text-muted uppercase font-mono">Email</th>
                <th className="text-left px-5 py-3 text-xs text-text-muted uppercase font-mono">Name</th>
                <th className="text-left px-5 py-3 text-xs text-text-muted uppercase font-mono">Tags</th>
                <th className="text-left px-5 py-3 text-xs text-text-muted uppercase font-mono">Subscribed</th>
                <th className="text-left px-5 py-3 text-xs text-text-muted uppercase font-mono">Status</th>
                <th className="text-right px-5 py-3 text-xs text-text-muted uppercase font-mono">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map(sub => (
                <tr key={sub.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`subscriber-row-${sub.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Mail size={14} className="text-cyber-yellow" />
                      <span className="text-white text-sm">{sub.email}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-text-secondary text-sm">{sub.first_name || '-'} {sub.last_name || ''}</td>
                  <td className="px-5 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {(sub.tags || []).map(tag => (
                        <span key={tag} className="text-xs px-2 py-0.5 bg-cyber-yellow/10 text-cyber-yellow rounded-full">{tag}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-text-muted text-xs font-mono">
                    {new Date(sub.subscribed_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${sub.is_active ? 'bg-laser-green/20 text-laser-green' : 'bg-red-500/20 text-red-400'}`}>
                      {sub.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => handleDelete(sub.id)} className="text-red-500 hover:text-red-400 text-sm">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
};

export default SubscribersManager;
